import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { ErrorState, LoadingState } from '../primitives';
import { useIntlLocale } from '../intl-locale';
import { formatters } from '@/usage/format';
import { useTimeLabel } from './format';
import type { AgentMakesVM, AgentRunVM, LibraryKind } from './types';

// Agent runs as Inbox rows, and the Agent page's "Makes and updates". An
// Agent's version publishes directly after its checks, so a row reports what
// happened and what the person can still do: Undo or Restore a published
// version, Fix in chat for a held draft, Review for a drop, write or new grant
// (those wait for a person), Choose who it runs as for a run with no person.
// Nothing here approves; callbacks open the real cards.

export interface AgentRunRowProps {
  run: AgentRunVM;
  onUndo?: (runId: string) => void;
  onRestorePrevious?: (runId: string) => void;
  onFixInChat?: (runId: string) => void;
  onLookAt?: (runId: string) => void;
  onReview?: (runId: string) => void;
  onChooseWho?: (runId: string) => void;
  onChangeInChat?: (runId: string) => void;
  onRetry?: (runId: string) => void;
}

const CONFIRM_KEY = {
  drop: 'agentConfirmDrop',
  write: 'agentConfirmWrite',
  grant: 'agentConfirmGrant',
} as const;

export function AgentRunRow({
  run,
  onUndo,
  onRestorePrevious,
  onFixInChat,
  onLookAt,
  onReview,
  onChooseWho,
  onChangeInChat,
  onRetry,
}: AgentRunRowProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const time = useTimeLabel();
  const { outcome } = run;
  const base = { agent: run.agentName, title: run.artifactTitle };
  const when = time(run.at);
  const locale = useIntlLocale();
  const list = (values: string[]) =>
    new Intl.ListFormat(locale, { type: 'conjunction' }).format(values);
  const meta = when ? <small className="ap-agent-when">{when}</small> : null;
  return (
    <article className="ap-agent-row" data-outcome={outcome.status} aria-label={run.artifactTitle}>
      {outcome.status === 'published' && (
        <Callout
          tone="success"
          actions={
            <>
              {outcome.canUndo && (
                <Button type="button" variant="ghost" size="sm" onClick={() => onUndo?.(run.id)}>
                  {copy.agentUndo}
                </Button>
              )}
              {outcome.canRestorePrevious && outcome.previousNumber !== null && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onRestorePrevious?.(run.id)}
                >
                  {formatCopy(copy.agentRestorePrevious, {
                    number: String(outcome.previousNumber),
                  })}
                </Button>
              )}
            </>
          }
        >
          {meta}
          {formatCopy(copy.agentPublished, { ...base, number: String(outcome.number) })}{' '}
          {formatCopy(copy.agentCheckedOn, {
            views: list(
              outcome.checkedViews.viewports.map(
                (v) => copy[`agentView${v[0].toUpperCase()}${v.slice(1)}` as keyof typeof copy],
              ),
            ),
            themes: list(
              outcome.checkedViews.themes.map(
                (t) => copy[`agentTheme${t[0].toUpperCase()}${t.slice(1)}` as keyof typeof copy],
              ),
            ),
          })}
        </Callout>
      )}
      {outcome.status === 'held' && (
        <Callout
          tone="warning"
          actions={
            <>
              {outcome.canFix && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onFixInChat?.(run.id)}
                >
                  {copy.agentFixInChat}
                </Button>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={() => onLookAt?.(run.id)}>
                {formatCopy(copy.agentLookAt, { number: String(outcome.number) })}
              </Button>
            </>
          }
        >
          {meta}
          {formatCopy(copy.agentHeld, { ...base, number: String(outcome.number) })}{' '}
          {outcome.reason === 'mismatch' && outcome.count !== null
            ? formatCopy(copy.agentHeldMismatch, { count: String(outcome.count) })
            : copy.agentHeldCheck}
        </Callout>
      )}
      {outcome.status === 'confirm' && (
        <Callout
          tone="warning"
          actions={
            outcome.canReview ? (
              <Button type="button" size="sm" onClick={() => onReview?.(run.id)}>
                {copy.agentReview}
              </Button>
            ) : undefined
          }
        >
          {meta}
          {outcome.kind === 'drop' && outcome.count !== null
            ? formatCopy(copy.agentConfirmDropCount, {
                ...base,
                what: outcome.what,
                count: String(outcome.count),
              })
            : formatCopy(copy[CONFIRM_KEY[outcome.kind]], { ...base, what: outcome.what })}
        </Callout>
      )}
      {outcome.status === 'skipped' && (
        <Callout
          tone="warning"
          actions={
            <>
              {outcome.canChooseWho && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onChooseWho?.(run.id)}
                >
                  {copy.agentChooseWho}
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChangeInChat?.(run.id)}
              >
                {copy.agentChangeInChat}
              </Button>
            </>
          }
        >
          {meta}
          {formatCopy(copy.agentSkipped, { agent: run.agentName, part: outcome.part })}{' '}
          {outcome.system && formatCopy(copy.agentSkippedWhy, { system: outcome.system })}
        </Callout>
      )}
      {outcome.status === 'failed' && (
        <Callout
          tone="danger"
          actions={
            outcome.canRetry ? (
              <Button type="button" variant="outline" size="sm" onClick={() => onRetry?.(run.id)}>
                {copy.agentRetry}
              </Button>
            ) : undefined
          }
        >
          {meta}
          {formatCopy(copy.agentFailed, base)} {copy.agentFailedBody}
        </Callout>
      )}
      {outcome.status === 'running' && (
        <Callout tone="info">
          {meta}
          {formatCopy(copy.agentRunning, base)}
        </Callout>
      )}
    </article>
  );
}

const KIND_KEY: Record<LibraryKind, string> = {
  report: 'kindReport',
  page: 'kindPage',
  site: 'kindSite',
  deck: 'kindDeck',
  document: 'kindDocument',
  spreadsheet: 'kindSpreadsheet',
  dashboard: 'kindDashboard',
  table: 'kindTable',
  chart: 'kindChart',
  markdown: 'kindMarkdown',
  kpi: 'kindKpi',
  app: 'kindApp',
};

export function AgentMakesPanel({
  vm,
  onRetry,
  rowHandlers,
}: {
  vm: AgentMakesVM;
  onRetry?: () => void;
  rowHandlers?: Omit<AgentRunRowProps, 'run'>;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const locale = useIntlLocale();
  return (
    <section className="ap-panel" aria-labelledby="ap-agent-makes-title" data-panel="agent-makes">
      <header className="ap-head">
        <h2 id="ap-agent-makes-title">{copy.agentMakesTitle}</h2>
      </header>
      {vm.load === 'loading' && <LoadingState label={copy.agentMakesLoading} />}
      {vm.load === 'error' && <ErrorState message={copy.agentMakesErrorBody} onRetry={onRetry} />}
      {vm.load === 'ready' && (
        <>
          {vm.makes.length === 0 ? (
            <p>{copy.agentMakesNone}</p>
          ) : (
            <ul className="ap-list">
              {vm.makes.map((item, index) => (
                <li key={`${item.kind}-${index}`} className="ap-row">
                  <span className="ap-row-copy">
                    <strong>
                      {item.title ?? copy[KIND_KEY[item.kind] as keyof typeof copy]}
                      <StatusPill>{copy[KIND_KEY[item.kind] as keyof typeof copy]}</StatusPill>
                    </strong>
                    <span>
                      {item.mode === 'update' ? copy.agentMakesUpdate : copy.agentMakesNew}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p>
            {vm.nextRun
              ? formatCopy(copy.agentNextRun, { when: vm.nextRun })
              : copy.agentNoSchedule}
          </p>
          <p>
            {vm.creditsPerRun !== null
              ? formatCopy(copy.agentCreditsPerRun, {
                  credits: formatters(locale).amount(vm.creditsPerRun),
                })
              : copy.agentCreditsUnknown}
          </p>
          {vm.lastRun && (
            <div>
              <p className="ap-eyebrow">{copy.agentLastResult}</p>
              <AgentRunRow run={vm.lastRun} {...rowHandlers} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
